/**
 * Shared presentational building blocks for the AI Ads Studio and
 * AI Movies & Series tiles. Keeps the two pages lean while enforcing the
 * same reference-anchored discipline:
 *   - Immutable asset chips (locked, user cannot retype).
 *   - Visible credit meter in every button that charges.
 *   - Approve / revise-once gates on every still before motion is allowed.
 */
import { useMemo } from 'react';
import { Lock, CheckCircle2, Clapperboard, ImagePlus, UserCircle2, MapPin, Package } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const KIND_ICON = {
  character: UserCircle2,
  location: MapPin,
  product: Package,
};

export function AssetChip({ asset, selected, onToggle, 'data-testid': testId }) {
  const Icon = KIND_ICON[asset.kind] || Package;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        selected
          ? 'border-amber-400 bg-amber-500/15 text-amber-200'
          : 'border-neutral-700 bg-neutral-900/60 text-neutral-300 hover:border-neutral-500',
      )}
    >
      <Lock size={12} />
      <Icon size={14} />
      <span>@{asset.name.replace(/\s+/g, '_')}</span>
    </button>
  );
}

export function CreditPill({ cost, label, 'data-testid': testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-200"
    >
      <Clapperboard size={11} />
      {cost} cr {label || ''}
    </span>
  );
}

export function ApproveBadge({ approved }) {
  if (!approved) {
    return (
      <Badge variant="outline" className="border-neutral-700 bg-neutral-900/60 text-neutral-400">
        Awaiting approval
      </Badge>
    );
  }
  return (
    <Badge className="border-emerald-400/40 bg-emerald-500/15 text-emerald-200">
      <CheckCircle2 size={12} className="mr-1" /> Approved
    </Badge>
  );
}

export function GenerateStillButton({ onClick, loading, revision, 'data-testid': testId }) {
  return (
    <Button
      onClick={onClick}
      disabled={loading}
      data-testid={testId}
      size="sm"
      className="bg-amber-500 text-neutral-900 hover:bg-amber-400"
    >
      <ImagePlus size={14} className="mr-1" />
      {loading ? 'Generating…' : revision ? 'Revise still — 1 cr' : 'Generate still — 1 cr'}
    </Button>
  );
}

export function useAspectDurationCost({ previews, duration, resolution }) {
  return useMemo(() => {
    const key = `${duration}:${resolution}`;
    return previews[key]?.credits ?? '…';
  }, [previews, duration, resolution]);
}
